import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Trophy, Eye, Edit, Trash2, Crown } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import teamImageService from '../services/teamImageService';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';
import { btn } from '../../utils/buttonStyles';

const TeamCard = ({
  team,
  onClick,
  onEdit,
  baseUrl = '/teams',
  onDelete,
  showActions = true,
  showDivider = false
}) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();
  const [logoUrl, setLogoUrl] = useState(null);

  const teamName = team.team_name || team.teamName || team.name;
  const teamLogo = team.team_logo || team.teamLogo || team.logo;
  const maxMembers = team.max_members || team.maxMembers || 0;
  const memberCount = team.members?.length || team._count?.members || 0;
  const isNewFormat = teamLogo?.startsWith('/images/');
  const isLegacyFile = !isNewFormat && teamLogo && teamLogo.includes('.');

  // Load logo URL for legacy bare-filename format only
  useEffect(() => {
    if (isLegacyFile && team.id) {
      teamImageService.getTeamLogoUrl(team.id, teamLogo)
        .then(url => {
          if (url) setLogoUrl(url);
        })
        .catch(err => console.error('Failed to load team logo:', err));
    }
  }, [team.id, teamLogo, isLegacyFile]);

  // Render the logo
  const renderLogo = () => {
    if (isNewFormat) {
      return <img src={resolveImageUrl(teamLogo)} alt={teamName} className="w-full h-full object-cover rounded-xl" />;
    } else if (isLegacyFile && logoUrl) {
      return <img src={logoUrl} alt={teamName} className="w-full h-full object-cover rounded-xl" />;
    } else if (teamLogo && !isLegacyFile) {
      return <span className="text-2xl">{teamLogo}</span>;
    } else {
      return teamName?.charAt(0)?.toUpperCase() || 'T';
    }
  };

  const isOwner = () => {
    return user && (
      team.createdBy === user.id ||
      team.ownerId === String(user.id) ||
      team.owner_id === String(user.id)
    );
  };

  const sportName = team.sportType?.sport_name || team.sportType?.sportName || team.sport || null;

  const handleViewClick = (e) => {
    e.stopPropagation();
    if (onClick) {
      onClick(team);
    } else {
      navigate(`${baseUrl}/${team.id || team.teamId}`);
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(team);
    } else {
      navigate(`${baseUrl}/${team.id || team.teamId}/edit`);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(team.id || team.teamId, teamName);
    }
  };

  return (
    <div>
      <div className={`${theme.card} rounded-2xl p-6 hover:shadow-lg transition-all duration-300 border ${theme.border}`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-x-4">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
              {renderLogo()}
            </div>
            <div className="flex-1">
              <h3 className={`font-bold text-lg ${theme.text} mb-1`}>{teamName}</h3>
              <p className={`${theme.textSecondary} text-sm line-clamp-2`}>
                {team.description || 'No description available'}
              </p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            team.is_active !== false && team.isActive !== false
              ? 'bg-green-100 text-green-600'
              : 'bg-gray-100 text-gray-600'
          }`}>
            {team.is_active !== false && team.isActive !== false ? 'Active' : 'Inactive'}
          </div>
        </div>

        <div className="flex items-center flex-wrap gap-4 mb-4">
          <div className={`flex items-center gap-x-1 text-sm ${theme.textSecondary}`}>
            <Users className="w-4 h-4" />
            <span>{memberCount}/{maxMembers} members</span>
          </div>
          {sportName && (
            <div className={`flex items-center gap-x-1 text-sm ${theme.textSecondary}`}>
              <Trophy className="w-4 h-4" />
              <span>{sportName}</span>
            </div>
          )}
          {team.captain && (
            <div className={`flex items-center gap-x-1 text-sm ${theme.textSecondary}`}>
              <Crown className="w-4 h-4" />
              <span>
                {team.captain.first_name} {team.captain.last_name}
                {user && (team.captain.id === user.id || team.captain.id === String(user.id)) && <span className="ml-1 text-xs text-blue-400">(You)</span>}
              </span>
            </div>
          )}
        </div>

        {showActions && (
          <div className={`${btn.group} justify-end`}>
            <button
              onClick={handleViewClick}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-1`}
            >
              <Eye className="w-3 h-3" />
              <span>View</span>
            </button>

            {isOwner() && (
              <>
                <button
                  onClick={handleEditClick}
                  className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-1`}
                >
                  <Edit className="w-3 h-3" />
                  <span>Edit</span>
                </button>

                {onDelete && (
                  <button
                    onClick={handleDeleteClick}
                    className={`${btn.base} ${btn.md} ${theme.buttonDanger} flex items-center gap-1`}
                  >
                    <Trash2 className="w-3 h-3" />
                    <span>Delete</span>
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {showDivider && (
        <div className={`border-t ${theme.border} my-4`}></div>
      )}
    </div>
  );
};

export default TeamCard;
